import { Layout } from '@/components/layout';
import { useGetDashboard, useListFlows, useListStores, useGetMe } from '@workspace/api-client-react';
import { useAuthHeaders, useAuthStore } from '@/lib/auth-store';
import { useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Store, Workflow, Activity, Clock, ArrowRight,
  RefreshCw, Package, DollarSign, ShoppingCart, Link2, Plus, Zap,
  CreditCard, ExternalLink, Sparkles,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Flow } from '@workspace/api-zod';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ─── helpers ────────────────────────────────────────────────────────────────

const ACTION_META: Record<string, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  sync_inventory: { label: 'Inventory', icon: RefreshCw,   color: 'text-sky-400',    bg: 'bg-sky-400/10'    },
  sync_products:  { label: 'Products',  icon: Package,      color: 'text-violet-400', bg: 'bg-violet-400/10' },
  sync_prices:    { label: 'Prices',    icon: DollarSign,   color: 'text-amber-400',  bg: 'bg-amber-400/10'  },
  sync_orders:    { label: 'Orders',    icon: ShoppingCart, color: 'text-rose-400',   bg: 'bg-rose-400/10'   },
};

function greeting() {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 21) return 'Good evening';
  return 'Hello';
}

function relTime(d: Date | string | null | undefined) {
  if (!d) return null;
  try { return formatDistanceToNow(new Date(d as any), { addSuffix: true }); } catch { return null; }
}

function firstName(name?: string | null) {
  return name?.split(' ')[0] ?? 'there';
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatCard({
  dot, label, value, sub, highlight = false, dimmed = false,
}: {
  dot: string; label: string; value: string | number; sub: string;
  highlight?: boolean; dimmed?: boolean;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3 hover:border-border/80 transition-colors shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </p>
      </div>
      <p className={`text-4xl font-display font-bold leading-none tabular-nums ${
        dimmed ? 'text-muted-foreground' : highlight ? 'text-primary' : 'text-foreground'
      }`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function StoreBadge({ store }: { store: any }) {
  const isActive = store.status === 'active';
  const isError  = store.status === 'error';
  const dotCls   = isActive ? 'bg-primary shadow-[0_0_5px_rgba(47,230,168,0.6)]'
                 : isError  ? 'bg-rose-500'
                 :            'bg-muted-foreground';
  return (
    <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-xl px-3 py-2">
      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground truncate max-w-[120px]">{store.nickname}</p>
        <p className="text-[10px] capitalize text-muted-foreground">{store.status ?? 'unknown'}</p>
      </div>
    </div>
  );
}

function ScheduledFlowRow({ flow }: { flow: Flow }) {
  const meta = ACTION_META[flow.actionType] ?? { label: flow.actionType, icon: Workflow, color: 'text-muted-foreground', bg: 'bg-muted/50' };
  const Icon = meta.icon;
  const src = flow.sourceStore?.nickname ?? 'Source';
  const dst = flow.destStore?.nickname ?? 'Destination';
  return (
    <div className="flex items-center gap-4 py-3.5 border-b border-border/50 last:border-0 hover:bg-muted/20 -mx-5 px-5 transition-colors">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}>
        <Icon className={`w-4 h-4 ${meta.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{flow.name}</p>
        <p className="text-xs flex items-center gap-1 mt-0.5 text-muted-foreground">
          <span className="truncate">{src}</span>
          <ArrowRight className="w-3 h-3 shrink-0" />
          <span className="truncate">{dst}</span>
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {flow.schedule && (
          <span className="hidden sm:flex items-center gap-1 text-[10px] bg-muted/60 px-2 py-1 rounded-md font-mono text-muted-foreground">
            <Clock className="w-3 h-3" />{flow.schedule}
          </span>
        )}
        <span className={`text-[10px] font-semibold px-2 py-1 rounded-md ${meta.bg} ${meta.color}`}>
          {meta.label}
        </span>
      </div>
    </div>
  );
}

function RunRow({ log }: { log: any }) {
  const isOk  = log.status === 'success';
  const isErr = log.status === 'error';
  const dotCls  = isOk  ? 'bg-primary shadow-[0_0_5px_rgba(47,230,168,0.5)]'
                : isErr ? 'bg-rose-500 shadow-[0_0_5px_#f43f5e]'
                :         'bg-amber-400 animate-pulse';
  const textCls = isOk  ? 'text-primary'
                : isErr ? 'text-rose-400' : 'text-amber-400';
  return (
    <div className="flex items-center gap-4 py-3.5 border-b border-border/50 last:border-0 hover:bg-muted/20 -mx-5 px-5 transition-colors">
      <div className={`w-2 h-2 rounded-full shrink-0 ${dotCls}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{log.flowName ?? 'Unknown Flow'}</p>
        <p className="text-xs mt-0.5 text-muted-foreground">
          {log.itemsCount != null ? `${log.itemsCount} items synced` : log.message ?? ''}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[10px] font-semibold capitalize ${textCls}`}>{log.status}</span>
        <span className="text-[10px] text-muted-foreground">{relTime(log.createdAt)}</span>
      </div>
    </div>
  );
}

// ─── banners ─────────────────────────────────────────────────────────────────

function formatAUD(cents: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(cents / 100);
}

function SubscribeBanner({ planName, planPriceCents, onSubscribe }: {
  planName: string;
  planPriceCents: number | null;
  onSubscribe: () => void;
}) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 mb-6 rounded-2xl border border-primary/30 bg-primary/5">
      <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">
          Activate your <span className="text-primary">{planName}</span> plan
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {planPriceCents
            ? `Subscribe for ${formatAUD(planPriceCents)}/month to unlock all features on your account.`
            : 'Subscribe to unlock all features on your account.'}
        </p>
      </div>
      <button
        onClick={onSubscribe}
        className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-[0_0_12px_rgba(47,230,168,0.2)]"
      >
        Subscribe now <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
}

const PAYMENT_REQUIRED_STATUSES = new Set(['past_due', 'unpaid', 'incomplete', 'incomplete_expired']);

function PaymentRequiredBanner({ onUpdateBilling, loading }: { onUpdateBilling: () => void; loading: boolean }) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10">
      <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
        <CreditCard className="w-4 h-4 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-600 dark:text-amber-300">Payment required</p>
        <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-0.5">
          Your account has an outstanding balance. Update your billing information to keep your automations running.
        </p>
      </div>
      <button
        onClick={onUpdateBilling}
        disabled={loading}
        className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Opening…' : (
          <>Update billing <ExternalLink className="w-3 h-3" /></>
        )}
      </button>
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export function Dashboard() {
  const [, setLocation] = useLocation();
  const authOpts = useAuthHeaders();
  const { token } = useAuthStore();
  const { data }         = useGetDashboard({ request: authOpts });
  const { data: flows }  = useListFlows({ request: authOpts });
  const { data: stores } = useListStores({ request: authOpts });
  const { data: me }     = useGetMe({ request: authOpts });

  // Billing status — check for past-due / unpaid subscription
  const { data: billing } = useQuery({
    queryKey: ['billing-me'],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/billing/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${BASE}/api/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      if (!r.ok) throw new Error('Could not open billing portal');
      return r.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => { window.open(data.url, '_blank', 'noopener'); },
  });

  const subStatus: string | undefined = billing?.subscription?.status;
  const paymentRequired = subStatus ? PAYMENT_REQUIRED_STATUSES.has(subStatus) : false;
  const needsSubscription = !!(billing?.planName && !billing?.subscription);

  const activeFlows = (flows ?? []).filter(f => f.isActive);
  const pausedFlows = (flows ?? []).filter(f => !f.isActive);
  const recentLogs  = data?.recentLogs ?? [];
  const hasError    = !!data?.lastError;

  const statsRow = [
    {
      dot: 'bg-primary shadow-[0_0_6px_rgba(47,230,168,0.5)]',
      label: 'Active Flows',
      value: data?.activeFlows ?? '—',
      sub: 'Scheduled & running',
      highlight: true,
    },
    {
      dot: 'bg-muted-foreground',
      label: 'Paused Flows',
      value: pausedFlows.length,
      sub: 'Not currently running',
      dimmed: true,
    },
    {
      dot: 'bg-primary/60',
      label: 'Runs Today',
      value: data?.runsToday ?? '—',
      sub: 'Completed syncs',
    },
    {
      dot: hasError ? 'bg-rose-500 shadow-[0_0_6px_#f43f5e]' : 'bg-muted-foreground',
      label: 'Errors',
      value: hasError ? 1 : 0,
      sub: hasError ? 'Requires attention' : 'All clear',
      dimmed: !hasError,
    },
  ];

  return (
    <Layout>
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground mb-1.5">
            {greeting()}, {firstName(me?.name)}
          </h1>
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground">
              {activeFlows.length > 0
                ? `${activeFlows.length} active flow${activeFlows.length !== 1 ? 's' : ''} across ${stores?.length ?? 0} store${(stores?.length ?? 0) !== 1 ? 's' : ''}`
                : 'Connect a store and create a flow to get started.'}
            </p>
            <span className={`hidden sm:flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border ${
              hasError
                ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                : 'bg-primary/10 border-primary/20 text-primary'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${hasError ? 'bg-rose-500' : 'bg-primary animate-pulse'}`} />
              {hasError ? 'Sync errors detected' : 'All systems operational'}
            </span>
          </div>
        </div>

        {(me as any)?.featureFlags?.flows !== false && (
          <button
            onClick={() => setLocation('/flows')}
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shadow-[0_0_16px_rgba(47,230,168,0.2)]"
          >
            <Plus className="w-4 h-4" />
            New Flow
          </button>
        )}
      </div>

      {/* ── Subscribe banner (plan assigned, no subscription yet) ── */}
      {needsSubscription && !paymentRequired && (
        <SubscribeBanner
          planName={billing!.planName!}
          planPriceCents={billing?.planPriceCents ?? null}
          onSubscribe={() => setLocation('/settings/billing')}
        />
      )}

      {/* ── Payment-required banner ───────────────────────────────── */}
      {paymentRequired && (
        <PaymentRequiredBanner
          onUpdateBilling={() => portalMutation.mutate()}
          loading={portalMutation.isPending}
        />
      )}

      {/* ── Stat cards ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statsRow.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </div>

      {/* ── Connected stores banner ───────────────────────────────── */}
      {(stores?.length ?? 0) > 0 && (
        <div className="bg-card border border-border rounded-2xl px-5 py-4 mb-6 flex items-center gap-4 flex-wrap shadow-sm">
          <div className="flex items-center gap-2 shrink-0">
            <Store className="w-4 h-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Connected Stores
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {stores?.map(s => <StoreBadge key={s.id} store={s} />)}
          </div>
        </div>
      )}

      {/* ── Bottom panels ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

        {/* Scheduled Flows */}
        <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Scheduled Flows</h2>
            </div>
            <span className="text-[10px] bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded-full font-semibold">
              {activeFlows.length} active
            </span>
          </div>
          <p className="text-xs mb-5 text-muted-foreground">Your currently enabled automations</p>

          {activeFlows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-10 h-10 rounded-xl bg-muted/60 flex items-center justify-center mb-3">
                <Link2 className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm mb-1 text-muted-foreground">No active flows yet</p>
              <p className="text-xs text-muted-foreground/70">Create a flow and enable it to see it here</p>
            </div>
          ) : (
            activeFlows.map(f => <ScheduledFlowRow key={f.id} flow={f} />)
          )}
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Recent Activity</h2>
            </div>
            <span className="text-[10px] bg-muted/60 border border-border px-2 py-0.5 rounded-full font-medium text-muted-foreground">
              {recentLogs.length} runs
            </span>
          </div>
          <p className="text-xs mb-5 text-muted-foreground">Latest sync results</p>

          {recentLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-10 h-10 rounded-xl bg-muted/60 flex items-center justify-center mb-3">
                <Activity className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm mb-1 text-muted-foreground">No runs yet</p>
              <p className="text-xs text-muted-foreground/70">Activity will appear here after your first sync</p>
            </div>
          ) : (
            recentLogs.map((log: any) => <RunRow key={log.id} log={log} />)
          )}
        </div>
      </div>
    </Layout>
  );
}
